import * as moment from 'moment';

/**
 * Pure helpers behind the MGVW (Maximum Gross Vehicle Weight) form.
 *
 * The form is entirely config-driven: the org's `contract_mgvw` custom_group
 * arrives on `/v1/appdata` as `dailyReportFields.contract_mgvw` with
 * `{ ui_config: { sections }, fields: custom_field[] }`. Nothing about the
 * fields is hardcoded here; this file only implements the `ui_config` keys
 * the mobile configFormRenderer honors, so both clients compute the same
 * allowable weights from the same config:
 *
 *   required          — mandatory (also accepted as validation_rules.required)
 *   display_only      — computed / read-only, never an input
 *   expression        — JS expression over the other field names, recomputed
 *                       on every change in display_order (dependencies come
 *                       earlier in the order)
 *   lookup_attr       — two-key table lookup into a lookup type
 *                       { lookup, value_field, attr_field, attr_transform }
 *   pick_list         — "<lookup type>[.value]" or a virtual source
 *                       (project_contractor, project_staff)
 *   pick_list_values  — inline static options
 *   numeric           — sort lookup options numerically ('10' after '3')
 *   default           — literal seed, or "today" on a date field
 *
 * The api stores `values` verbatim and never recomputes, so every computed
 * field is sent along with the inputs (AASHTOWare does not re-run its own
 * OnSave code for API-created instances either).
 */

export interface MgvwField {
  name: string;
  label?: string;
  type?: 'string' | 'number' | 'boolean' | 'date' | string;
  display_order?: number;
  ui_config?: any;
}

export interface MgvwSection {
  key: string;
  label: string;
  fields: MgvwField[];
}

export interface MgvwPickListOption {
  label: string;
  value: string;
  description?: string;
}

export interface MgvwPickListContext {
  lookups?: Record<string, any[]>;
  subContractors?: any[];
  accounts?: any[];
}

export const MGVW_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  aashtoware: 'From AASHTOWare',
};

/** Chip classes per status, in the RFI / change-order list vocabulary. */
export const MGVW_STATUS_MAP: Record<string, { label: string; bgColor: string; textColor: string }> = {
  draft: { label: MGVW_STATUS_LABELS.draft, bgColor: 'bg-draft', textColor: 'txt-draft' },
  submitted: { label: MGVW_STATUS_LABELS.submitted, bgColor: 'bg-approved', textColor: 'txt-approved' },
  aashtoware: { label: MGVW_STATUS_LABELS.aashtoware, bgColor: 'bg-aashtoware', textColor: 'txt-aashtoware' },
};

/** 'aashtoware' for inbound records, else the record's own status (draft by default). */
export function mgvwStatusKey(record: any): string {
  if (record?.origin === 'aashtoware') return 'aashtoware';
  return MGVW_STATUS_MAP[record?.status] ? record.status : 'draft';
}

export function sortFields(fields: MgvwField[] = []): MgvwField[] {
  return [...(fields || [])].sort(
    (a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0),
  );
}

export function isDisplayOnly(field: MgvwField): boolean {
  return !!field?.ui_config?.display_only;
}

export function isHidden(field: MgvwField): boolean {
  return !!field?.ui_config?.hidden;
}

export function isRequired(field: MgvwField): boolean {
  return !!(field?.ui_config?.required || field?.ui_config?.validation_rules?.required);
}

export function hasPickList(field: MgvwField): boolean {
  const inline = field?.ui_config?.pick_list_values;
  if (Array.isArray(inline)) return inline.length > 0;
  return !!field?.ui_config?.pick_list;
}

/**
 * Buckets fields into the group's `ui_config.sections` (ordered by the
 * section's own `fields` name list, which deliberately differs from
 * display_order: e.g. the mm tread width sits above the inch one). Fields no
 * section names land in a trailing "Details" bucket so nothing configured is
 * silently dropped.
 */
export function buildSections(group: any): MgvwSection[] {
  const fields: MgvwField[] = sortFields(group?.fields || []).filter(f => !isHidden(f));
  const byName = new Map(fields.map(f => [f.name, f]));
  const placed = new Set<string>();
  const sections: MgvwSection[] = [];

  const configured: any[] = Array.isArray(group?.ui_config?.sections) ? group.ui_config.sections : [];
  configured.forEach((section, idx) => {
    const names: string[] = Array.isArray(section?.fields) ? section.fields : [];
    const sectionFields = names
      .map(name => byName.get(name))
      .filter((f): f is MgvwField => !!f && !placed.has(f.name));
    sectionFields.forEach(f => placed.add(f.name));
    if (sectionFields.length) {
      sections.push({
        key: `section-${idx}`,
        label: section?.label || 'Details',
        fields: sectionFields,
      });
    }
  });

  const leftovers = fields.filter(f => !placed.has(f.name));
  if (leftovers.length) {
    sections.push({ key: 'section-rest', label: sections.length ? 'Details' : (group?.label || 'Details'), fields: leftovers });
  }
  return sections;
}

/** '' / null / undefined → null; numeric strings → numbers; 'true'/'false' → booleans. */
export function coerceExpressionValue(value: any): any {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (!Number.isNaN(Number(trimmed))) return Number(trimmed);
  }
  return value;
}

type ExpressionFn = (...args: unknown[]) => unknown;

const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
const compiledExpressions = new Map<string, ExpressionFn | null>();

const EXPRESSION_HELPERS: Record<string, ExpressionFn> = {
  round: (n: any, places: any = 0) => {
    const factor = Math.pow(10, Number(places) || 0);
    return Math.round(Number(n) * factor) / factor;
  },
  abs: (n: any) => Math.abs(Number(n)),
  min: (...args: any[]) => Math.min(...args.map(Number)),
  max: (...args: any[]) => Math.max(...args.map(Number)),
};

/**
 * Evaluates a config expression with every field name in scope as a variable
 * (same contract as mobile's configFormRenderer: `new Function` over the
 * field names plus round/abs helpers). Any throw, undefined or non-finite
 * number yields null so a half-filled form never shows NaN.
 */
export function evaluateExpression(
  expression: string,
  values: Record<string, any>,
  fieldNames: string[],
): any {
  if (!expression || typeof expression !== 'string') return null;
  const names = fieldNames.filter(n => IDENTIFIER.test(n));
  const helperNames = Object.keys(EXPRESSION_HELPERS);
  const cacheKey = `${names.join(',')}|${expression}`;

  let fn = compiledExpressions.get(cacheKey);
  if (fn === undefined) {
    try {
      fn = new Function(...names, ...helperNames, `"use strict"; return (${expression});`) as ExpressionFn;
    } catch {
      fn = null;
    }
    compiledExpressions.set(cacheKey, fn);
  }
  if (!fn) return null;

  try {
    const result = fn(
      ...names.map(n => coerceExpressionValue(values?.[n])),
      ...helperNames.map(h => EXPRESSION_HELPERS[h]),
    );
    if (result === undefined) return null;
    if (typeof result === 'number' && !Number.isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}

/**
 * `lookup_attr` — e.g. the Base BF GVW table: rows of lookup type
 * `mgvw_bfgvw` keyed by axle count (`value`) whose `extra_attributes` map
 * wheelbase feet → pounds. Returns null when either key misses the table.
 */
export function lookupAttrValue(
  config: any,
  values: Record<string, any>,
  lookups: Record<string, any[]> = {},
): any {
  if (!config?.lookup || !config?.value_field || !config?.attr_field) return null;
  const rows = lookups?.[config.lookup] || [];
  const rowKey = values?.[config.value_field];
  if (rowKey === null || rowKey === undefined || rowKey === '') return null;
  const row = rows.find(r => String(r?.value) === String(rowKey));
  if (!row) return null;

  let attr = values?.[config.attr_field];
  if (attr === null || attr === undefined || attr === '') return null;
  if (config.attr_transform === 'int') {
    const n = Number(attr);
    if (!Number.isFinite(n)) return null;
    attr = String(Math.floor(n));
  }
  const hit = row?.extra_attributes?.[String(attr)];
  return hit === undefined || hit === null ? null : hit;
}

/**
 * Recomputes every derived field, walking in display_order so a computed
 * value (base_bfgvw) is fresh before the expression that reads it
 * (a_allowable_bf_gvw). Editable fields with an expression (the inch tread
 * width) are recomputed too; their config keeps the typed value when the
 * driving input is blank.
 */
export function recomputeDerivedValues(
  fields: MgvwField[],
  values: Record<string, any>,
  lookups: Record<string, any[]> = {},
): Record<string, any> {
  const ordered = sortFields(fields);
  const names = ordered.map(f => f.name);
  const next: Record<string, any> = { ...values };
  ordered.forEach(field => {
    const cfg = field?.ui_config || {};
    if (cfg.lookup_attr) {
      next[field.name] = lookupAttrValue(cfg.lookup_attr, next, lookups);
    } else if (typeof cfg.expression === 'string' && cfg.expression.trim()) {
      next[field.name] = evaluateExpression(cfg.expression, next, names);
    }
  });
  return next;
}

/** The seed for a new record: literal defaults, "today" for dates, null otherwise. */
export function defaultValueFor(field: MgvwField): any {
  const def = field?.ui_config?.default;
  if (def === undefined) return null;
  if (field?.type === 'date' && def === 'today') return moment().format('YYYY-MM-DD');
  return def;
}

const DATE_PARSE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601];

export function parseMgvwDate(value: any): moment.Moment | null {
  if (value === null || value === undefined || value === '') return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const parsed = moment(value, DATE_PARSE_FORMATS as any, true);
  if (parsed.isValid()) return parsed;
  const loose = moment(value);
  return loose.isValid() ? loose : null;
}

/**
 * Turns stored `values` (or nothing, for a new record) into what the form
 * controls hold: booleans as booleans, dates as moments, everything else as
 * stored. Missing keys get the config default.
 */
export function buildFormSeed(fields: MgvwField[], values: Record<string, any> = {}): Record<string, any> {
  const seed: Record<string, any> = {};
  (fields || []).forEach(field => {
    const stored = values ? values[field.name] : undefined;
    let v = stored === undefined ? defaultValueFor(field) : stored;
    if (field.type === 'boolean') {
      v = v === true || v === 'true' || v === 'Yes';
    } else if (field.type === 'date') {
      v = parseMgvwDate(v);
    } else if (v === '') {
      v = null;
    }
    seed[field.name] = v;
  });
  return seed;
}

/**
 * The `values` object sent to the api: one key per configured field
 * (computed ones included), typed the way mobile sends them so a record
 * edited on either client round-trips identically.
 */
export function buildPayloadValues(fields: MgvwField[], raw: Record<string, any> = {}): Record<string, any> {
  const values: Record<string, any> = {};
  (fields || []).forEach(field => {
    let v = raw ? raw[field.name] : undefined;
    if (field.type === 'boolean') {
      v = v === true || v === 'true' || v === 'Yes';
    } else if (field.type === 'date') {
      const parsed = parseMgvwDate(v);
      v = parsed ? parsed.format('YYYY-MM-DD') : null;
    } else if (v === '' || v === undefined) {
      v = null;
    } else if (field.type === 'number' && v !== null) {
      const n = Number(v);
      v = Number.isFinite(n) ? n : null;
    }
    values[field.name] = v;
  });
  return values;
}

/** Project lookups win over org lookups per type when they have rows. */
export function buildLookupBag(appLookups: any, projectLookups: any): Record<string, any[]> {
  const bag: Record<string, any[]> = {};
  Object.entries(appLookups || {}).forEach(([type, rows]) => {
    if (Array.isArray(rows)) bag[type] = rows;
  });
  Object.entries(projectLookups || {}).forEach(([type, rows]) => {
    if (Array.isArray(rows) && rows.length) bag[type] = rows;
  });
  return bag;
}

function lookupLabel(row: any): string {
  const extra = row?.extra_attributes || {};
  return String(extra.name || extra.label || extra.code || row?.name || (row?.value ?? ''));
}

/**
 * Options for a pick-list field. Values are always strings: AASHTOWare ids
 * for the two virtual sources, the lookup `value` otherwise. The dropdown
 * compares by string so a stored number still selects.
 */
export function resolvePickListOptions(field: MgvwField, ctx: MgvwPickListContext = {}): MgvwPickListOption[] {
  const cfg = field?.ui_config || {};
  const inline = cfg.pick_list_values;
  if (Array.isArray(inline) && inline.length) {
    return inline.map((v: any) =>
      typeof v === 'string'
        ? { label: v, value: v }
        : { label: String(v?.label ?? v?.name ?? v?.value ?? ''), value: String(v?.value ?? v?.name ?? '') },
    );
  }

  const type: string | undefined = typeof cfg.pick_list === 'string' ? cfg.pick_list.split('.')[0] : undefined;
  if (!type) return [];

  if (type === 'project_contractor') {
    return (ctx.subContractors || [])
      .filter(sc => sc?.source_entity_id !== null && sc?.source_entity_id !== undefined && String(sc.source_entity_id) !== '')
      .sort((a, b) => (Number(!!b?.prime) - Number(!!a?.prime))
        || String(a?.name || a?.company_name || '').localeCompare(String(b?.name || b?.company_name || '')))
      .map(sc => ({
        label: `${sc?.name || sc?.company_name || '(unnamed)'}${sc?.prime ? ' (Prime)' : ''}`,
        value: String(sc.source_entity_id),
        description: sc?.prime ? 'Prime' : undefined,
      }));
  }

  if (type === 'project_staff') {
    return (ctx.accounts || [])
      .filter(a => a?.source_entity_id !== null && a?.source_entity_id !== undefined && String(a.source_entity_id) !== '')
      .map(a => {
        const name = `${a?.first_name || ''} ${a?.last_name || ''}`.trim() || a?.email || '(unnamed)';
        const userName = a?.source_entity_extra_attributes?.user_name;
        return {
          label: userName ? `${name} (${userName})` : name,
          value: String(a.source_entity_id),
          description: userName || undefined,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  const rows = ctx.lookups?.[type] || [];
  const options = rows
    .filter(r => r?.value !== null && r?.value !== undefined && String(r.value) !== '')
    .map(r => ({ label: lookupLabel(r), value: String(r.value) }));
  if (cfg.numeric) {
    options.sort((a, b) => Number(a.value) - Number(b.value));
  }
  return options;
}

/** Vendor / person ids stored on a record → the display name the pick list would show. */
export function pickListLabelFor(field: MgvwField, value: any, ctx: MgvwPickListContext = {}): string {
  if (value === null || value === undefined || value === '') return '';
  const hit = resolvePickListOptions(field, ctx).find(o => o.value === String(value));
  return hit?.label ?? String(value);
}

/** The date shown next to a record's status, mirroring the mobile card. */
export function mgvwStatusDate(record: any): string {
  const entered = record?.values?.entered_date;
  if (record?.origin === 'aashtoware' && entered) {
    const parsed = parseMgvwDate(entered);
    if (parsed) return parsed.format('MMM D, YYYY');
  }
  const ts = (record?.status === 'submitted' && record?.submitted_at) || record?.updated_at || record?.created_at;
  const m = ts ? moment(ts) : null;
  return m && m.isValid() ? m.format('MMM D, YYYY') : '';
}

export function mgvwStatusLabel(record: any): string {
  return MGVW_STATUS_MAP[mgvwStatusKey(record)].label;
}

export function isMgvwReadOnly(record: any): boolean {
  return record?.status === 'submitted' || record?.origin === 'aashtoware';
}

/** The names of required fields left blank, as labels for the error toast. */
export function missingRequiredLabels(fields: MgvwField[], raw: Record<string, any>): string[] {
  return (fields || [])
    .filter(f => isRequired(f) && !isDisplayOnly(f))
    .filter(f => {
      const v = raw?.[f.name];
      return v === null || v === undefined || v === '' || (typeof v === 'string' && v.trim() === '');
    })
    .map(f => f.label || f.name);
}

/** 32-char hex client uuid: the api's idempotency key for POST /v1/mgvw-record. */
export function newClientUuid(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null;
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, '');
  const bytes = new Array(16).fill(0).map(() => Math.floor(Math.random() * 256));
  return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
}
